import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/shared/config';
import { type LanguageCode, DEFAULT_LANGUAGE } from '@/shared/config/languages';

interface AppState {
  // Sidebar state
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  selectedMenuKey: string;
  openMenuKeys: string[];

  // Language state
  language: LanguageCode;

  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setSelectedMenuKey: (key: string) => void;
  setOpenMenuKeys: (keys: string[]) => void;
  setLanguage: (language: LanguageCode) => void;
}

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        sidebarCollapsed: false,
        mobileSidebarOpen: false,
        selectedMenuKey: '/',
        openMenuKeys: [],
        language: DEFAULT_LANGUAGE,

        // Actions
        toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

        setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

        setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),

        setSelectedMenuKey: (key) => set({ selectedMenuKey: key }),

        setOpenMenuKeys: (keys) => set({ openMenuKeys: keys }),

        setLanguage: (language) => set({ language }),
      }),
      {
        name: STORAGE_KEYS.APP_STATE,
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          openMenuKeys: state.openMenuKeys,
          language: state.language,
        }),
      }
    ),
    { name: 'AppStore' }
  )
);
